/**
 * Home / Dashboard — the warm "family at a glance" landing surface that replaces
 * land-on-contacts (FAMILY-PIVOT-UX §4.1). Opened many times a day, so it leads
 * with what changes most: today/this-week across all the kids, the kid avatar
 * row, the shopping "to buy" count, household costs this month and the latest
 * note — plus a single quick-add.
 *
 * Mode-aware: in `'separated'` mode it adds a small "this week — with you / other
 * parent" strip; family-mode copy stays warm and legal-vocabulary-free.
 *
 * No business logic lives in components elsewhere; this surface only *reads*
 * existing api-client methods (calendar, shopping, expenses, messages, children)
 * and composes them from `components/ui` primitives. Calendar events are per
 * child, so we fan out one `listEvents` call per child and merge the results.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CalendarEvent, Expense, Message, ShoppingItem } from '@solandia/core';
import { formatMoney } from '@solandia/core';
import { useI18n } from '../../i18n';
import { useApi } from '../../api/ApiProvider';
import { useFamily } from '../../family/FamilyContext';
import { useAsync } from '../../hooks/useAsync';
import { makePeopleResolver } from '../../family/people';
import {
  Badge,
  EmptyState,
  ErrorState,
  SectionHeader,
  Spinner,
} from '../../components/ui';

/** A calendar event with its owning child stitched on for cross-kid display. */
interface DashboardEvent {
  event: CalendarEvent;
  childId: string;
  childName: string;
  childEmoji: string;
}

/** Start of the local day for a date, as ms since epoch. */
function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** First name only — the greeting is personal, not formal. */
function firstName(displayName: string): string {
  const trimmed = displayName.trim();
  if (!trimmed) return '';
  return trimmed.split(/\s+/)[0] ?? '';
}

/** Pick the right greeting key for the local hour. */
function greetingKey(hour: number): 'greetingMorning' | 'greetingDay' | 'greetingEvening' {
  if (hour < 11) return 'greetingMorning';
  if (hour < 18) return 'greetingDay';
  return 'greetingEvening';
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('sk-SK', { hour: '2-digit', minute: '2-digit' });
}

export function HomeModule() {
  const { t } = useI18n();
  const api = useApi();
  const navigate = useNavigate();
  const { family, me, membership, children, setActiveChild } = useFamily();

  const people = useMemo(
    () => makePeopleResolver(me, t.expenses.you),
    [me, t.expenses.you],
  );

  // -- Calendar across all kids: fan out one listEvents per child, then merge --
  const childKey = useMemo(() => children.map((c) => c.id).join(','), [children]);
  const {
    data: events,
    loading: eventsLoading,
    error: eventsError,
    reload: reloadEvents,
  } = useAsync<DashboardEvent[]>(async () => {
    if (children.length === 0) return [];
    const lists = await Promise.all(
      children.map(async (child) => {
        const res = await api.listEvents(family.id, child.id);
        return res.items.map<DashboardEvent>((event) => ({
          event,
          childId: child.id,
          childName: child.name,
          childEmoji: child.avatarEmoji,
        }));
      }),
    );
    return lists.flat();
  }, [family.id, childKey]);

  // -- Shopping: count unchecked items across every list ----------------------
  const {
    data: toBuy,
    loading: shoppingLoading,
    error: shoppingError,
  } = useAsync<number>(async () => {
    const lists = await api.listShoppingLists(family.id);
    if (lists.items.length === 0) return 0;
    const itemLists = await Promise.all(
      lists.items.map((l) => api.listShoppingItems(l.id)),
    );
    return itemLists
      .flatMap((r) => r.items)
      .filter((i: ShoppingItem) => !i.checked).length;
  }, [family.id]);

  // -- Expenses this month ----------------------------------------------------
  const {
    data: expenses,
    loading: expensesLoading,
    error: expensesError,
  } = useAsync<Expense[]>(async () => {
    const res = await api.listExpenses(family.id);
    return res.items;
  }, [family.id]);

  // -- Latest note ------------------------------------------------------------
  const {
    data: latestNote,
    loading: noteLoading,
  } = useAsync<Message | null>(async () => {
    const res = await api.listMessages(family.id, { limit: 1 });
    const sorted = [...res.items].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return sorted[0] ?? null;
  }, [family.id]);

  // -- Derived: today / this-week buckets -------------------------------------
  const now = new Date();
  const todayStart = startOfDay(now);
  const tomorrowStart = todayStart + 86_400_000;
  const weekEnd = todayStart + 7 * 86_400_000;

  const { todayEvents, tomorrowCount } = useMemo(() => {
    const upcoming = (events ?? [])
      .filter((e) => new Date(e.event.startAt).getTime() >= todayStart)
      .sort((a, b) => a.event.startAt.localeCompare(b.event.startAt));
    const today = upcoming.filter((e) => {
      const t0 = new Date(e.event.startAt).getTime();
      return t0 >= todayStart && t0 < tomorrowStart;
    });
    const tomorrow = upcoming.filter((e) => {
      const t0 = new Date(e.event.startAt).getTime();
      return t0 >= tomorrowStart && t0 < tomorrowStart + 86_400_000;
    });
    return { todayEvents: today, tomorrowCount: tomorrow.length };
  }, [events, todayStart, tomorrowStart]);

  // Separated-mode strip: this week split by who created the event ("you" vs
  // the other parent). A light cross-household peek; family mode never sees it.
  const weekSplit = useMemo(() => {
    if (family.mode !== 'separated') return null;
    let mine = 0;
    let other = 0;
    for (const e of events ?? []) {
      const t0 = new Date(e.event.startAt).getTime();
      if (t0 < todayStart || t0 >= weekEnd) continue;
      if (people.isMe(e.event.createdById)) mine += 1;
      else other += 1;
    }
    return { mine, other };
  }, [events, family.mode, people, todayStart, weekEnd]);

  const monthCosts = useMemo(() => {
    const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const inMonth = (expenses ?? []).filter((e) => e.incurredOn.startsWith(ym));
    let totalMinor = 0;
    let currency = 'EUR';
    let outstanding = 0;
    for (const e of inMonth) {
      totalMinor += e.amountMinor;
      currency = e.currency;
      if (!e.settledAt) outstanding += 1;
    }
    return { totalMinor, currency, outstanding };
    // `now` is recreated each render but its month is stable across a render set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenses]);

  // A family is "empty" (warm setup state) when it has no kids and no notes and
  // nothing bought-yet — i.e. nothing has been set up at all.
  const anyLoading =
    eventsLoading || shoppingLoading || expensesLoading || noteLoading;
  const isEmpty =
    !anyLoading &&
    children.length === 0 &&
    (toBuy ?? 0) === 0 &&
    (expenses?.length ?? 0) === 0 &&
    !latestNote;

  const greeting = t.home[greetingKey(now.getHours())].replace(
    '{name}',
    firstName(me.user.displayName) || t.home.greetingNeutral,
  );

  function go(slug: string) {
    navigate(`/m/${slug}`);
  }
  function openChild(childId: string) {
    setActiveChild(childId);
    navigate('/m/children');
  }

  return (
    <section className="stack">
      <SectionHeader
        title={`${greeting} ☀️`}
        action={
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => go('calendar')}
          >
            ＋ {t.home.add}
          </button>
        }
      />

      {isEmpty ? (
        <HomeEmptyState
          onAddChild={() => go('children')}
          onInvite={membership.role === 'parent' ? () => go('sharing') : undefined}
          onCreateList={() => go('shopping')}
        />
      ) : (
        <div className="form-grid form-grid-2">
          {/* --- Today (across all kids) --- */}
          <article className="card stack-sm" aria-labelledby="home-today">
            <div className="row-between">
              <h3 id="home-today" className="section-title">
                📅 {t.home.todayTitle}
              </h3>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => go('calendar')}>
                {t.home.viewAll}
              </button>
            </div>

            {eventsLoading ? <Spinner /> : null}
            {eventsError ? (
              <ErrorState message={t.errors.generic} onRetry={reloadEvents} retryLabel={t.common.retry} />
            ) : null}

            {!eventsLoading && !eventsError ? (
              todayEvents.length === 0 ? (
                <p className="text-sm muted">{t.home.nothingToday}</p>
              ) : (
                <ul className="list">
                  {todayEvents.map((e) => (
                    <li key={e.event.id} className="row" style={{ gap: 'var(--sol-space-sm)' }}>
                      <span className="text-sm" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                        {e.event.allDay ? t.common.today : fmtTime(e.event.startAt)}
                      </span>
                      <span className="grow text-sm">
                        <span aria-hidden="true">{e.childEmoji} </span>
                        {e.event.title}
                      </span>
                      <span className="text-xs muted">{e.childName}</span>
                    </li>
                  ))}
                </ul>
              )
            ) : null}

            {tomorrowCount > 0 ? (
              <p className="text-xs muted">
                {t.home.tomorrowEvents.replace('{count}', String(tomorrowCount))}
              </p>
            ) : null}

            {weekSplit ? (
              <div className="row wrap" aria-label={t.custody.thisWeek} style={{ gap: 'var(--sol-space-sm)' }}>
                <Badge tone="primary">
                  {t.expenses.you}: {weekSplit.mine}
                </Badge>
                <Badge tone="accent">
                  {people.label('')}: {weekSplit.other}
                </Badge>
              </div>
            ) : null}
          </article>

          {/* --- Kids + Shopping --- */}
          <article className="card stack-sm" aria-labelledby="home-kids">
            <div className="row-between">
              <h3 id="home-kids" className="section-title">
                👧 {t.home.childrenTitle}
              </h3>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => go('children')}>
                {t.home.viewAll}
              </button>
            </div>

            {children.length === 0 ? (
              <p className="text-sm muted">{t.home.noChildrenYet}</p>
            ) : (
              <div className="pills" role="list">
                {children.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    role="listitem"
                    className="pill"
                    onClick={() => openChild(c.id)}
                  >
                    <span className="pill-emoji" aria-hidden="true">
                      {c.avatarEmoji}
                    </span>
                    {c.name}
                  </button>
                ))}
                <button
                  type="button"
                  className="pill"
                  onClick={() => go('children')}
                  aria-label={t.children.add}
                >
                  ＋
                </button>
              </div>
            )}

            <div className="row-between" style={{ marginTop: 'var(--sol-space-sm)' }}>
              <span className="text-sm">🛒 {t.home.shoppingTitle}</span>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => go('shopping')}>
                {t.home.viewAll}
              </button>
            </div>
            {shoppingLoading ? (
              <Spinner />
            ) : shoppingError ? (
              <p className="text-sm muted">{t.home.noShoppingYet}</p>
            ) : (toBuy ?? 0) > 0 ? (
              <p className="text-sm">
                <strong>{t.shopping.itemCount.replace('{count}', String(toBuy))}</strong>{' '}
                <span className="muted">{t.shopping.markBought.toLowerCase()}</span>
              </p>
            ) : (
              <p className="text-sm muted">{t.home.noShoppingYet}</p>
            )}
          </article>

          {/* --- Household costs this month --- */}
          <article className="card stack-sm" aria-labelledby="home-costs">
            <div className="row-between">
              <h3 id="home-costs" className="section-title">
                💶 {t.home.expensesTitle}
              </h3>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => go('expenses')}>
                {t.home.viewAll}
              </button>
            </div>

            {expensesLoading ? (
              <Spinner />
            ) : expensesError ? (
              <p className="text-sm muted">{t.home.noExpensesYet}</p>
            ) : monthCosts.totalMinor > 0 ? (
              <>
                <p className="text-sm">
                  {t.home.thisMonth}:{' '}
                  <strong>{formatMoney(monthCosts.totalMinor, monthCosts.currency)}</strong>
                </p>
                {family.mode === 'separated' && monthCosts.outstanding > 0 ? (
                  <p className="text-xs">
                    <Badge tone="danger">
                      {t.home.outstandingCount.replace('{count}', String(monthCosts.outstanding))}
                    </Badge>
                  </p>
                ) : null}
              </>
            ) : (
              <p className="text-sm muted">{t.home.noExpensesYet}</p>
            )}
          </article>

          {/* --- Latest note --- */}
          <article className="card stack-sm" aria-labelledby="home-note">
            <div className="row-between">
              <h3 id="home-note" className="section-title">
                📝 {t.home.lastNoteTitle}
              </h3>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => go('messages')}>
                {t.home.viewAll}
              </button>
            </div>

            {noteLoading ? (
              <Spinner />
            ) : latestNote ? (
              <blockquote className="stack-sm" style={{ margin: 0 }}>
                <p className="text-sm">„{latestNote.body}“</p>
                <p className="text-xs muted">
                  — {people.label(latestNote.authorId)} ·{' '}
                  {new Date(latestNote.createdAt).toLocaleDateString('sk-SK')}
                </p>
              </blockquote>
            ) : (
              <p className="text-sm muted">{t.home.noNotesYet}</p>
            )}
          </article>
        </div>
      )}
    </section>
  );
}

/**
 * Warm, populated empty state — three setup cards for a brand-new family.
 * The invite card is only shown to a parent (trusted members can't invite).
 */
function HomeEmptyState({
  onAddChild,
  onInvite,
  onCreateList,
}: {
  onAddChild: () => void;
  onInvite?: () => void;
  onCreateList: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="stack">
      <EmptyState emoji="👋" title={t.home.emptyTitle} hint={t.home.emptyLead} />
      <div className="form-grid form-grid-2">
        <SetupCard emoji="👧" label={t.home.emptyAddChild} onClick={onAddChild} />
        {onInvite ? (
          <SetupCard emoji="👨‍👩‍👧" label={t.home.emptyInvitePartner} onClick={onInvite} />
        ) : null}
        <SetupCard emoji="🛒" label={t.home.emptyCreateList} onClick={onCreateList} />
      </div>
    </div>
  );
}

function SetupCard({
  emoji,
  label,
  onClick,
}: {
  emoji: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="card card-tight row"
      onClick={onClick}
      style={{ textAlign: 'left', cursor: 'pointer', gap: 'var(--sol-space-md)' }}
    >
      <span aria-hidden="true" style={{ fontSize: '1.6rem' }}>
        {emoji}
      </span>
      <span className="grow" style={{ fontWeight: 600 }}>
        {label}
      </span>
      <span aria-hidden="true" className="muted">
        →
      </span>
    </button>
  );
}
